/**
 * How long a newly added photo shows — STUDIO_DESIGN.md §9 item 2 and §6.8's empty state:
 * "4 seconds each, one photo alone gets 8".
 *
 * This rule lives HERE, in the frontend, because xconfig cannot express a default that depends on
 * the number of inputs — the Phase 0 ruling says so in as many words and deferred it to Phase 5
 * (`STUDIO_PLAN_P0/progress.md:9,58`). The Studio never writes `imageDuration` (master `:24`); it
 * writes `holdMs` on the clip it creates, which is the same intent in the composition's own terms.
 *
 * The sentence these two numbers promise is rendered by `files-tray.component.tsx` from
 * `COPY.emptyDropBody`, and `hold-defaults.spec.ts` asserts the milliseconds against it: the copy
 * and the constants are one decision written twice, so they are guarded in both directions.
 */
import type { PhotoClip, StudioDocument } from '../model/composition';
import { DEFAULT_PHOTO_MOTION } from './motion';
import type { MotionKind } from './motion';

export const DEFAULT_HOLD_MS = 4000;

export const SOLO_HOLD_MS = 8000;

// --------------------------------------------------------------------- S-31: the shared hold

/**
 * The "Same for every photo" policy (STUDIO_FIX_PROMPT S-31).
 *
 * `null` is OFF, the default. A number is ON: every photo clip shows for this long, and every photo
 * placed WHILE it stays on inherits it -- which is why the policy lives here, beside the function
 * the placement path already calls, rather than in the inspector that flips it. The inspector owns
 * the switch; `holdForNewClips` owns the consequence; the two never have to be wired together.
 *
 * IT IS SESSION STATE, NOT DOCUMENT STATE, on purpose. The document is the wire shape and history's
 * unit, and "keep them the same from now on" is an intent about FUTURE edits, not a fact about the
 * composition the server renders. It is therefore not persisted and not undoable: turning it off
 * changes no clip, and an Undo after turning it on restores the holds but leaves the switch where
 * the user left it (the ledger row names this).
 *
 * Module-level rather than on a component because the placement path (`studio-tab` `place`) and the
 * switch (`inspector.component`) are two components with no shared owner short of the tab itself,
 * and the wave brief forbids this slice from editing the tab. One Studio per page keeps this honest;
 * `clearSharedHold` is what a project switch should call.
 */
let sharedHoldMs: number | null = null;

/** The hold every photo shares while the policy is on, else `null`. */
export function sharedHold(): number | null {
    return sharedHoldMs;
}

/** Turn the policy on at `holdMs`, or off with `null`. Idempotent. */
export function setSharedHold(holdMs: number | null): void {
    sharedHoldMs = holdMs === null || !Number.isFinite(holdMs) ? null : Math.round(holdMs);
}

/** Off. For a project switch, and for every spec's `beforeEach`. */
export function clearSharedHold(): void {
    sharedHoldMs = null;
}

/**
 * The document with `holdMs` on every photo clip; the SAME object when no photo changes, so an
 * owner can use identity to skip a history entry (the `withClampedTransition` convention). Video
 * clips keep their own length -- `COPY.otherClipsKeepLength` says so.
 */
export function applySharedHold(doc: StudioDocument, holdMs: number): StudioDocument {
    let changed = false;
    const clips = doc.clips.map((c) => {
        if (c.kind !== 'PHOTO' || c.holdMs === holdMs) return c;
        changed = true;
        return { ...c, holdMs };
    });
    return changed ? { ...doc, clips } : doc;
}

// --------------------------------------------------------------------- the placement defaults

/**
 * The hold for `addedCount` photos arriving into a project that already has `existingCount` clips.
 *
 * With the shared-hold policy ON (S-31) every new photo gets the shared hold, whatever the counts
 * say: "same for every photo" includes the ones that have not arrived yet.
 *
 * Otherwise SOLO only for the case the copy describes: an empty project receiving exactly one
 * photo. Two photos into an empty project is a slideshow, and a slideshow's first photo is not
 * special.
 */
export function holdForNewClips(existingCount: number, addedCount: number): number {
    if (sharedHoldMs !== null) return sharedHoldMs;
    return existingCount === 0 && addedCount === 1 ? SOLO_HOLD_MS : DEFAULT_HOLD_MS;
}

/** What a NEW photo clip is born with, beyond its `input`. */
export interface NewPhotoDefaults {
    holdMs: number;
    motion: MotionKind;
}

/**
 * Every default a NEW photo clip carries (STUDIO_FIX_PROMPT S-30, wave-brief seam 2; motion
 * default reversed by QA round 2, Q5).
 *
 * `motion` is `DEFAULT_PHOTO_MOTION` -- Still (`NONE`) since Q5: a photo moves only when the user
 * picks Slow zoom or Pan in the inspector, and `COPY.motionExplainer` says so. It is still written
 * EXPLICITLY on the clip rather than left to the wire or to hydration, so the placement path
 * carries one decision and this function is where a future default change lands. The wire
 * projection omits `NONE` like every other schema default (`toWire`), and a saved project that
 * never had the key keeps rendering as it did (`fromWire` does not invent it; `composition.spec.ts`
 * pins that). Only a clip born here is a new clip.
 */
export function defaultsForNewPhotoClip(existingCount: number, addedCount: number): NewPhotoDefaults {
    return { holdMs: holdForNewClips(existingCount, addedCount), motion: DEFAULT_PHOTO_MOTION };
}

/** The clip itself, for a placement path that would otherwise spell the literal. */
export function newPhotoClip(input: number, existingCount: number, addedCount: number): PhotoClip {
    return { input, kind: 'PHOTO', ...defaultsForNewPhotoClip(existingCount, addedCount) };
}

/**
 * After an add, drop an untouched solo 8 s back to 4 s.
 *
 * Runs on the document AFTER the new clips are in it, so "is this still a one-photo project?" is
 * answered by what is on screen. Returns the same object when nothing changes, so a caller can use
 * identity to decide whether to commit a history entry (the convention `withClampedTransition`
 * already set in `model/composition.ts`).
 *
 * WHAT THIS LATCH CANNOT DO, stated plainly because the equality test looks like it can. `holdMs`
 * is a number: a first clip a user deliberately set to 8.0 s is byte-for-byte the solo default, so
 * adding a second clip silently rewrites it to 4 s. The latch cannot tell the two apart and no
 * amount of care here will make it.
 *
 * THE FIX IS THE OWNER'S, NOT THIS FUNCTION'S (P5c plan ruling 1; review I6 corrected this note,
 * which used to describe a different mechanism). `holdTouched` lives on `StudioUiState`, beside
 * `frameTouched` — NOT on the clip, which is a wire object and would carry a non-schema field into
 * the composition. This function's signature and body do not change when it lands: the owner gates
 * the CALL, skipping `relaxSoloHold` once the flag is set.
 *
 * AND THE OWNER MUST LATCH IT BY DIFFING `holdMs`. There is no "the user set this hold" signal in
 * P5b to reuse: `onFeatureUsed('hold')` fires once per feature per SESSION (`view-props.ts`'s
 * contract, design §8), so it is true for every later add as well and cannot be the latch. The two
 * ways a user changes a hold are the inspector's field/preset/stepper (`inspector.component.tsx`
 * `setHold`) and the track's own resize drag (`onResize` with a `clip-resize-*` coalesce key), and
 * both arrive at the owner's reducer as a new `holdMs` — so the reducer compares the incoming hold
 * with the stored one and sets `holdTouched` when they differ.
 *
 * Until that lands the accepted cost is: a deliberate 8.0 s on the FIRST clip only, lost on the
 * next add, restorable with one Undo. Every other clip is untouched, because only the first was
 * ever alone.
 */
export function relaxSoloHold(doc: StudioDocument): StudioDocument {
    if (doc.clips.length < 2) return doc;
    const first = doc.clips[0];
    if (first.kind !== 'PHOTO' || first.holdMs !== SOLO_HOLD_MS) return doc;
    // Only the FIRST clip can carry the solo default — it is the only one that was ever alone.
    const clips = doc.clips.slice();
    clips[0] = { ...first, holdMs: DEFAULT_HOLD_MS };
    return { ...doc, clips };
}
